import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle } from "lucide-react";
import { lazy } from "react";

import { SectionHeading } from "../components/SectionHeading";
import { ServiceCard } from "../components/ServiceCard";
import { ContactDetails } from "../components/ContactDetails";
import { CtaBand } from "../components/CtaBand";
import { LazyMount } from "../components/LazyMount";
import { diensten } from "../lib/data";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";

// Onder de vouw: pas ophalen wanneer de bezoeker in de buurt scrollt. Zo blijft
// de initiële JS op mobiel klein (zod-validatie en de reviews-query vallen weg).
const ContactForm = lazy(() =>
  import("../components/ContactForm").then((m) => ({ default: m.ContactForm })),
);
const GoogleReviews = lazy(() =>
  import("../components/GoogleReviews").then((m) => ({ default: m.GoogleReviews })),
);
const InstagramFeed = lazy(() =>
  import("../components/InstagramFeed").then((m) => ({ default: m.InstagramFeed })),
);
const AanbouwCalculator = lazy(() =>
  import("../components/AanbouwCalculator").then((m) => ({ default: m.AanbouwCalculator })),
);



// Hero-varianten (v2): kleinere stappen + zwaardere WebP-compressie. Het beeld
// ligt onder een donkere gradient, dus lagere kwaliteit is visueel onzichtbaar
// en scheelt op mobiel ~13 kB tegenover de oude 768w-variant.
const heroImage = "/__l5e/assets-v1/962715f2-f68a-4159-9954-0742df3f7122/hero-v2-1920.webp";
const heroSrcSet = [
  "/__l5e/assets-v1/dae0587a-0167-41d1-ab3e-74eb3123a2f0/hero-v2-480.webp 480w",
  "/__l5e/assets-v1/f636eee7-bd2a-4fed-b2f2-6eacd12f6b47/hero-v2-640.webp 640w",
  "/__l5e/assets-v1/d6ed3fa1-0bc7-4c66-83b0-69409a4727a2/hero-v3-768.webp 768w",
  "/__l5e/assets-v1/1e98afb8-aab3-4fec-8aae-25cad899fb01/hero-v2-1080.webp 1080w",
  "/__l5e/assets-v1/e4b47aab-d086-46d7-a4d1-f032cdb10331/hero-v2-1440.webp 1440w",
  `${heroImage} 1920w`,
].join(", ");
const subsidieImage =
  "/__l5e/assets-v1/4fbb1e01-6bc8-488c-afed-4272c4741e40/subsidie-isoleren.webp";
const subsidieImage768 =
  "/__l5e/assets-v1/4780aeb3-1fad-493c-900e-3171bf5b260a/subsidie-isoleren-768-v2.webp";

const faqs = [
  {
    q: "Wat kost een timmerman in Dordrecht?",
    a: "Een timmerman in Dordrecht rekent doorgaans € 55 tot € 75 per uur, exclusief materiaal. Voor projecten zoals een dakkapel, kozijnen of aanbouw werken we met een vaste projectprijs. Op onze kostenpagina's vind je actuele richtprijzen per dienst.",
  },
  {
    q: "In welke wijken van Dordrecht werkt Fedor Timmerwerken?",
    a: "We werken in heel Dordrecht, waaronder Het Reeland, Crabbehof, Dubbeldam, Zuidhoven, Sterrenburg en de Binnenstad, en daarnaast in Zwijndrecht, Papendrecht en Sliedrecht.",
  },
  {
    q: "Komt de timmerman gratis langs voor advies en een offerte?",
    a: "Ja. We komen vrijblijvend bij je thuis langs in Dordrecht en omgeving, bekijken de situatie ter plekke en sturen daarna een duidelijke offerte zonder verrassingen achteraf.",
  },
  {
    q: "Hoe snel kan een timmerklus in Dordrecht starten?",
    a: "Kleinere timmerwerken pakken we vaak binnen enkele weken op. Voor grotere projecten zoals een aanbouw of dakopbouw plannen we samen een startdatum, inclusief eventuele vergunning en levertijd van materialen.",
  },
];

// Meta-omschrijving van de site — één bron voor description, og:description en
// twitter:description (zelfde patroon als kosten.tsx en werk-in-beeld.tsx).
const siteDescription =
  "Timmerman in Dordrecht voor dakkapellen, kozijnen, aanbouw en renovatie op maat. 7+ jaar ervaring, gratis advies aan huis — vraag je offerte aan.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Timmerman Dordrecht | Timmerbedrijf Fedor Timmerwerken" },
      { name: "description", content: siteDescription },
      {
        property: "og:title",
        content: "Timmerman Dordrecht | Timmerbedrijf Fedor Timmerwerken",
      },
      {
        name: "twitter:title",
        content: "Timmerman Dordrecht | Timmerbedrijf Fedor Timmerwerken",
      },
      { property: "og:description", content: siteDescription },
      { name: "twitter:description", content: siteDescription },
      { property: "og:url", content: `${SITE_URL}/` },
      ...ogImageMeta(ogImageDefault, "Fedor de Vries voor de bedrijfsbus van Fedor Timmerwerken"),
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/` },
      {
        rel: "preload",
        as: "image",
        href: heroImage,
        imageSrcSet: heroSrcSet,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: HomePage,
});


const featuredSlugs = ["dakkapel", "kozijnen", "renovatie"];

function HomePage() {
  const featuredDiensten = featuredSlugs
    .map((slug) => diensten.find((d) => d.slug === slug))
    .filter((d): d is (typeof diensten)[number] => Boolean(d));

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            srcSet={heroSrcSet}
            sizes="100vw"
            alt="Fedor de Vries voor de bedrijfsbus van Fedor Timmerwerken"
            width={1920}
            height={960}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/70 to-background/85 sm:bg-gradient-to-r sm:from-background/85 sm:via-background/65 sm:to-background/30" />
        </div>

        <div className="container-tight relative py-14 sm:py-32 lg:py-40">
          <div className="max-w-2xl">
            <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary sm:text-sm">
              Timmer- &amp; aannemersbedrijf in Dordrecht
            </span>
            <h1 className="mt-4 font-heading text-[1.75rem] font-bold leading-[1.15] tracking-tight text-foreground sm:mt-6 sm:text-5xl lg:text-6xl">
              Timmerman in Dordrecht —{" "}
              <span className="text-primary">vakwerk</span> van Fedor Timmerwerken
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:mt-6 sm:text-lg">
              Op zoek naar een timmerman of timmerbedrijf in Dordrecht? Fedor Timmerwerken heeft 7+
              jaar ervaring met dakkapellen, kozijnen, aanbouwen en complete renovaties in Dordrecht
              en wijken zoals Het Reeland, Crabbehof, Dubbeldam en Zuidhoven — altijd ter plekke op
              maat gemaakt, zodat het resultaat perfect aansluit bij jouw wensen.
            </p>

            <div className="mt-6 flex flex-wrap gap-3 sm:mt-8 sm:gap-4">
              <Link
                to="/contact"
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
              >
                Vraag vrijblijvend een offerte aan
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/diensten"
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md border border-input bg-background/80 px-6 py-3 text-sm font-semibold text-foreground backdrop-blur-sm transition-colors hover:bg-accent sm:w-auto"
              >
                Bekijk onze diensten
              </Link>
            </div>

            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-muted-foreground sm:mt-10 sm:flex sm:flex-wrap sm:gap-x-6">
              {[
                "Ter plekke op maat gemaakt",
                "Gratis adviesgesprek aan huis",
                "Subsidieproof offertes",
                "Eén aanspreekpunt",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span className="min-w-0">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

      </section>

      {/* Onze diensten */}
      <section className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            eyebrow="Onze diensten"
            title="Laat ons je helpen je dromen waar te maken"
            description="Van dakkapel tot complete renovatie: ieder project maken we ter plekke op maat, passend bij jouw huis en jouw wensen."
            align="center"
          />

          <div className="mt-10 sm:mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featuredDiensten.map((dienst) => (
              <ServiceCard
                key={dienst.slug}
                title={dienst.title}
                description={dienst.description}
                imageSrc={dienst.imageSrc}
                imageSrc768={dienst.imageSrc768}
                imageAlt={dienst.imageAlt}
                slug={dienst.slug}
              />
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/diensten"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80"
            >
              Bekijk alle diensten
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Lokale focus */}
      <section className="border-t border-border/60 bg-secondary/20 py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <div className="mx-auto max-w-3xl text-center">
            <span className="font-heading text-sm font-semibold uppercase tracking-wider text-primary">
              Werkgebied
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Timmerman en timmerbedrijf in Dordrecht en de Drechtsteden
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Fedor Timmerwerken is als timmerbedrijf gevestigd in Dordrecht en werkt in de hele
              regio, van{" "}

              <Link to="/timmerman-dordrecht" className="text-primary hover:text-primary/80">
                Dordrecht
              </Link>{" "}
              tot{" "}
              <Link to="/timmerman-zwijndrecht" className="text-primary hover:text-primary/80">
                Zwijndrecht
              </Link>
              ,{" "}
              <Link to="/timmerman-papendrecht" className="text-primary hover:text-primary/80">
                Papendrecht
              </Link>{" "}
              en{" "}
              <Link to="/timmerman-sliedrecht" className="text-primary hover:text-primary/80">
                Sliedrecht
              </Link>
              . We komen graag bij je langs voor een gratis adviesgesprek aan huis.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Wil je eerst weten wat een uitbouw kost? Bekijk de{" "}
              <Link to="/aanbouw-kosten" className="text-primary hover:text-primary/80">
                aanbouw kosten per m² in 2026
              </Link>{" "}
              en reken je plan direct uit met onze rekenhulp.
            </p>
            <div className="mt-8">
              <Link
                to="/timmerman-dordrecht"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Bekijk ons werkgebied
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Subsidie */}
      <section className="border-t border-border/60 bg-secondary/20 py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <div className="grid gap-10 overflow-hidden rounded-2xl border border-border/60 bg-card lg:grid-cols-2">
            <div className="min-h-64">
              <img
                src={subsidieImage}
                srcSet={`${subsidieImage768} 768w, ${subsidieImage} 1200w`}
                sizes="(min-width: 1024px) 560px, calc(100vw - 2rem)"
                alt="Zolderisolatie als voorbeeld van een verduurzamingsmaatregel"
                width={1200}
                height={466}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center p-8 lg:p-12">
              <span className="font-heading text-sm font-semibold uppercase tracking-wider text-primary">
                Subsidie
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Profiteren van de subsidie van Dordt Isoleert?
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Wij plaatsen nieuwe kozijnen en isolerend glas én leveren offertes en facturen die
                geschikt zijn voor je subsidieaanvraag. Zo ben je verzekerd van vakwerk én een goede
                basis voor je aanvraag.
              </p>
            <div className="mt-8">
                <Link
                  to="/subsidie"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Meer informatie
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rekenhulp */}
      <section className="border-t border-border/60 py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            eyebrow="Rekenhulp"
            title="Bereken direct wat jouw aanbouw kost"
            description="Kies oppervlakte, bouwmethode en afwerkniveau en zie meteen een richtprijs per m² en het totaalbedrag — inclusief btw."
            align="center"
          />
          <div className="mx-auto mt-10 max-w-3xl sm:mt-14">
            <LazyMount minHeight={1100}>
              <AanbouwCalculator />
            </LazyMount>
          </div>
        </div>
      </section>

      {/* Werk in beeld — Instagram */}
      <section className="border-t border-border/60 bg-secondary/20 py-14 sm:py-20 lg:py-24">
        <div className="container-tight">
          <SectionHeading
            eyebrow="Werk in beeld"
            title="Ons vakwerk op Instagram"
            description="Foto's en video's van lopende en opgeleverde projecten — zo zie je met eigen ogen wat we maken."
            align="center"
          />

          <div className="mt-10 sm:mt-14">
            <LazyMount minHeight={420}>
              <InstagramFeed limit={6} />
            </LazyMount>
          </div>
        </div>
      </section>

      {/* Klantenreviews */}
      <section className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            eyebrow="Klantenreviews"
            title="Wat klanten over ons zeggen"
            align="center"
          />

          <div className="mt-10 sm:mt-14">
            <LazyMount minHeight={360}>
              <GoogleReviews />
            </LazyMount>
          </div>

        </div>
      </section>

      {/* Veelgestelde vragen */}
      <section className="border-t border-border/60 py-14 sm:py-20 lg:py-24">
        <div className="container-tight">
          <SectionHeading
            eyebrow="Veelgestelde vragen"
            title="Een timmerman in Dordrecht inhuren: dit vragen klanten ons"
            align="center"
          />

          <dl className="mx-auto mt-10 max-w-3xl divide-y divide-border/60 sm:mt-14">
            {faqs.map((f) => (
              <div key={f.q} className="py-5">
                <dt className="font-heading text-base font-semibold text-foreground sm:text-lg">
                  {f.q}
                </dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>


      {/* Contact */}
      <section className="border-t border-border/60 bg-secondary/20 py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            eyebrow="Contact"
            title="Je volgende project bespreken?"
            description="Vul het formulier in of bel ons voor een gratis adviesgesprek aan huis."
            align="center"
            className="mx-auto"
          />

          <div className="mt-10 sm:mt-14 grid gap-10 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <ContactDetails />
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-2xl border border-border/60 bg-card p-6 lg:p-8">
                <h2 className="font-heading text-xl font-semibold text-foreground">
                  Stuur een bericht
                </h2>
                <div className="mt-6">
                  <LazyMount minHeight={640}>
                    <ContactForm />
                  </LazyMount>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
